<template>
  <BlokkliProvider v-bind="blokkliProps">
    <div v-if="image && showImageInHero" class="bg-green-campaign-hero">
      <Container>
        <MediaImage
          v-if="mobileImage"
          v-bind="mobileImage"
          :image-style="mobileImageStyle"
          class="w-full relative z-100 md:hidden py-30"
        />
        <MediaImage
          v-bind="image"
          :image-style="imageStyle"
          :class="[
            'w-full relative z-100',
            mobileImage ? 'hidden md:block' : '',
          ]"
        />
      </Container>
    </div>
    <Container>
      <div class="lg:w-8/12 node">
        <SplitTitle
          v-slot="{ top, bottom }"
          :title="title"
          :bold-count="titleWords"
        >
          <ContentHeader
            :title="top"
            :title-light="bottom"
            :lead="summary"
            :icon="showImageInHero ? undefined : icon"
          >
            <div
              v-if="body"
              class="content mt-20 md:mt-30 lg:mt-50"
              v-html="body"
            />
          </ContentHeader>
        </SplitTitle>
      </div>
      <slot name="paragraphs-before" />
    </Container>

    <slot name="header-after" />

    <BlokkliField
      v-slot="{ items }"
      :list="paragraphs"
      name="field_paragraphs"
      non-empty-class="container is-content mx-auto px-20"
      :allowed-fragments="['anchor_nav']"
    >
      <TabNavigation
        v-if="showAnchorNavigation"
        :tabs="getTabs(items)"
        class="mb-20 md:mb-30 lg:mb-60"
      />
    </BlokkliField>

    <Container>
      <BlokkliField
        :list="paragraphsFooter"
        non-empty-class="paragraphs-field-list-footer"
        name="field_paragraphs_footer"
      />
      <BlokkliField
        :list="paragraphsContact"
        non-empty-class="paragraphs-field-list-contact mt-80"
        name="field_paragraphs_contact"
      />
      <div
        v-if="paywall?.showPaywall && siteName === 'pharmasuisse'"
        class="lg:w-8/12"
      >
        <Paywall v-bind="paywall" />
      </div>
    </Container>
  </BlokkliProvider>
</template>

<script lang="ts" setup>
import type { NodePageFragment } from '#graphql-operations'
import { falsy } from '#shared/helpers/type'
import { slugify } from '~/helpers/slugify'
import type { FieldListItemTyped } from '#blokkli-build/generated-types'

const { name: siteName } = useCurrentSite()

const imageStyle = defineImageStyle({
  type: 'sizes',
  aspectRatio: 1170 / 370,
  sizes: {
    sm: 400,
    md: 728,
    lg: 984,
    xl: 1170,
  },
})

const mobileImageStyle = defineImageStyle({
  type: 'sizes',
  aspectRatio: 1,
  sizes: {
    sm: 400,
    md: 728,
  },
})
const props = defineProps<NodePageFragment>()

useHead({
  bodyAttrs: {
    class: computed<string>(() =>
      props.showImageInHero ? 'hide-breadcrumbs' : '',
    ),
  },
})

function getTabs(items: FieldListItemTyped[]) {
  return items
    .filter((v) => {
      if (v.bundle === 'section_title' || v.bundle === 'numbered_section') {
        const value = v.options?.showInNavigation
        return value === '1'
      }
    })
    .map((v) => {
      if (v.bundle === 'section_title' || v.bundle === 'numbered_section') {
        const paragraph = v.props
        const label = paragraph.anchor || paragraph.title
        if (label) {
          return {
            label,
            id: slugify(label),
          }
        }
      }

      return null
    })
    .filter(falsy)
}
</script>
